import { useState } from 'react';
import _ from 'lodash';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  SORT_TYPE_PRESORT,
  DEFAULT_LABEL_VERSION_PRESORT,
  DEFAULT_LABEL_VERSION_FINAL,
} from 'constants/warehouseConfig';
import { updateWarehouseConfig } from 'repositories/HttpRepository';
import { parseRegionList, parseWarehouseIdList } from 'utils/warehouseTransitions';
import { showToastError } from 'utils/toastUtil';

const joinList = (value) => (Array.isArray(value) ? value.join(', ') : (value ?? ''));

const parsePrefixList = (value) =>
  String(value ?? '')
    .split(',')
    .map((prefix) => prefix.trim().toUpperCase())
    .filter(Boolean);

export const makeTransition = () => ({
  sort_type: SORT_TYPE_PRESORT,
  target_regions: '',
  target_warehouses: '',
  delivery_days: [],
  label_version: DEFAULT_LABEL_VERSION_PRESORT,
  _originalIndex: null,
});

export const configToForm = (config, warehouse) => {
  const isNewConfig = _.isEmpty(config);
  const source = isNewConfig ? {} : config;

  return {
    active: isNewConfig ? true : (source.active ?? false),
    label_version: source.label_version ?? '',
    label_prefixes: Array.isArray(source.label_prefixes)
      ? source.label_prefixes.join(',')
      : (source.label_prefixes ?? ''),
    warehouse_id: source.warehouse_id ?? warehouse?.id,
    region: source.region ?? warehouse?.region,
    // The backend replaces the whole document on save, so every field of the loaded transition has
    // to be spread back in — the four the dashboard never renders are exactly the ones that would
    // be destroyed by rebuilding the row from the edited keys alone.
    transitions: (source.transitions ?? []).map((transition, index) => ({
      ...transition,
      sort_type: transition.sort_type ?? SORT_TYPE_PRESORT,
      target_regions: joinList(transition.target_regions),
      target_warehouses: joinList(transition.target_warehouses),
      delivery_days: transition.delivery_days ?? [],
      label_version: transition.label_version ?? '',
      _originalIndex: index,
    })),
  };
};

const isPresent = (value) => {
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null;
};

const required = (label) => (value) => (isPresent(value) ? true : `${label} is required`);

export const CONFIG_RULES = {
  label_version: { validate: required('Label Version') },
  label_prefixes: { validate: required('Label Prefixes') },
};

const TRANSITION_RULES = {
  sort_type: { validate: required('Sort Type') },
  label_version: { validate: required('Label Version') },
  target_regions: {
    validate: (value) => (parseRegionList(value).regions.length > 0 ? true : 'Target Regions is required'),
  },
  target_warehouses: {
    validate: (value) => {
      const { invalidTokens } = parseWarehouseIdList(value);
      return invalidTokens.length === 0 ? true : `Invalid warehouse IDs: ${invalidTokens.join(', ')}`;
    },
  },
};

export const transitionRules = (field) => TRANSITION_RULES[field] ?? {};

export const isDefaultLabelVersion = (value) =>
  !value || value === DEFAULT_LABEL_VERSION_PRESORT || value === DEFAULT_LABEL_VERSION_FINAL;

export const findClearedTransitions = (formTransitions, loadedTransitions) => {
  const rows = formTransitions ?? [];
  const loaded = loadedTransitions ?? [];
  const survivingKeys = new Set(rows.map((row) => row._originalIndex).filter((index) => index != null));

  const clearedByEdit = rows
    .filter((row) => row._originalIndex != null)
    .map((row) => ({ row, original: loaded[row._originalIndex] }))
    .filter(
      ({ row, original }) =>
        original?.target_warehouses?.length > 0 && parseWarehouseIdList(row.target_warehouses).ids.length === 0,
    )
    .map(({ row, original }) => ({
      index: row._originalIndex,
      sortType: row.sort_type,
      regions: parseRegionList(row.target_regions).regions,
      count: original.target_warehouses.length,
    }));

  const clearedByDelete = loaded
    .map((original, index) => ({ original, index }))
    .filter(({ original, index }) => original?.target_warehouses?.length > 0 && !survivingKeys.has(index))
    .map(({ original, index }) => ({
      index,
      sortType: original.sort_type,
      regions: original.target_regions ?? [],
      count: original.target_warehouses.length,
    }));

  return [...clearedByEdit, ...clearedByDelete];
};

export const formToConfig = (values, loadedConfig) => ({
  ...loadedConfig,
  ...values,
  label_prefixes: parsePrefixList(values.label_prefixes),
  transitions: (values.transitions ?? []).map((transition) => ({
    ..._.omit(transition, '_originalIndex'),
    target_regions: parseRegionList(transition.target_regions).regions,
    target_warehouses: parseWarehouseIdList(transition.target_warehouses).ids,
  })),
});

export default function useWarehouseConfigForm({ config, warehouse, onSaved }) {
  const { control, handleSubmit, formState, setValue, getValues } = useForm({
    defaultValues: configToForm(config, warehouse),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'transitions', keyName: 'key' });
  const [submitting, setSubmitting] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);

  const addTransition = () => append(makeTransition());

  const applyDefaultLabelVersion = (index, sortType) => {
    // RHF marks a freshly appended row dirty wholesale, so dirty state cannot distinguish a
    // factory default from a value the user typed. Comparing against the known defaults can.
    if (!isDefaultLabelVersion(getValues(`transitions.${index}.label_version`))) return;
    const labelVersion = sortType === SORT_TYPE_PRESORT ? DEFAULT_LABEL_VERSION_PRESORT : DEFAULT_LABEL_VERSION_FINAL;
    // reValidateMode only covers a field's own onChange, so without this a "required" error raised
    // by an earlier submit would survive the value being filled in, keeping SAVE disabled.
    setValue(`transitions.${index}.label_version`, labelVersion, { shouldValidate: true });
  };

  const postConfig = (payload) => {
    setSubmitting(true);
    return updateWarehouseConfig(warehouse.id, payload)
      .then((res) => {
        if (res.ok) {
          toast.success('Update successfully!');
          onSaved(res.data);
          return;
        }
        showToastError(res);
      })
      .catch((error) => showToastError(error?.response))
      .finally(() => setSubmitting(false));
  };

  const submit = handleSubmit((values) => {
    const payload = formToConfig(values, config);
    const clearedTransitions = findClearedTransitions(values.transitions, config?.transitions);

    if (clearedTransitions.length > 0) {
      setPendingSave({ payload, clearedTransitions });
      return undefined;
    }

    return postConfig(payload);
  });

  const confirmClearAndSave = () => postConfig(pendingSave.payload).then(() => setPendingSave(null));

  const keepEditing = () => {
    if (!submitting) setPendingSave(null);
  };

  return {
    control,
    formState,
    fields,
    addTransition,
    removeTransition: remove,
    submitting,
    pendingSave,
    getValues,
    applyDefaultLabelVersion,
    submit,
    confirmClearAndSave,
    keepEditing,
    onSaved,
  };
}
